import { useState } from 'react';
import { useT } from '../i18n/locale-context';
import { detectInAppBrowser } from '../lib/in-app-browser';
import { TelegramIcon } from './icons';
import {
  TelegramBotLogin,
  type TelegramBotAuthResult,
  type TelegramBotSurface,
} from './telegram-bot-login';

/**
 * THE «Подключить Telegram» button. One component, every surface that asks for
 * the connection: the catalog onboarding strip, the onboarding modal, the
 * dashboard connect card and the catalog banner.
 *
 * ## Why it wears Telegram's colours and not ours
 *
 * This is the only control on the site painted in a foreign brand's blue
 * (`telegram` in the Tailwind palette, #2AABEE → #229ED9). That is the point:
 * the user is being asked to hand over a THIRD-PARTY account, and a teal
 * button asking for it reads as us, not as them (Igor, 2026-09-11 — «в стиле
 * телеграма, с иконкой, чтобы было доверие»). Everything else — height,
 * radius, weight, type scale — is our own button vocabulary, so it still
 * belongs on the page.
 *
 * The plane is an inline SVG (`TelegramIcon`), never a remote asset: the whole
 * reason this button exists is that telegram.org is throttled by several UZ
 * ISPs, so a logo fetched from their CDN would be missing exactly when the
 * user most needs to recognise the button.
 *
 * ## Through the bot (owner, 2026-10-18)
 *
 * The tap opens the bot exactly like «Войти через Telegram» does
 * (`TelegramBotLogin`): the same request, the same 15 minutes, the same
 * renewal, the same Android intent and in-app hint. Start in the bot IS the
 * connection — no confirm step, and no telegram.org widget with its phone-
 * number window any more.
 *
 * Telegram's Login Widget used to be stretched over this span at `opacity: 0`,
 * because a bare `t.me/<bot>?start=` deep link matched on `telegram_id` alone
 * and would have given a signed-in e-mail user a SECOND account. That is not
 * what this does: the `start` parameter is a one-time request THIS browser
 * opened, and the link is made by this browser's own poll, with its own session
 * (`POST /auth/telegram/bot/status` → `loginOrLinkTelegramViaBot`: a real
 * account LINKS, and `telegram_account_taken` is refused exactly as before).
 * The «one widget per page» ownership table (`SURFACE_PRIORITY`) went with the
 * widget: any number of these buttons may be on a page at once.
 */
export function TelegramConnectButton({
  botUsername,
  surface,
  compact = false,
  block = false,
  onBotAuth,
  onBotError,
}: {
  botUsername: string;
  /** Start was pressed and the server linked Telegram to this session — the
   *  host re-reads it (`completeTelegramBotLogin`) and moves on. */
  onBotAuth: (result: TelegramBotAuthResult) => void | Promise<void>;
  /** A refusal to explain — 409 `telegram_account_taken` above all. */
  onBotError?: (err: unknown) => void;
  surface: TelegramBotSurface;
  /** The one-line onboarding strip, where the row has ~155px to spare. Matches
   *  «Указать» on the ИНН row rather than the full-height CTA. */
  compact?: boolean;
  /** Stretch to the container — the modal step and the dashboard card. */
  block?: boolean;
}) {
  const t = useT();
  // Which browser this is, read once (the hand-off and the in-app hint).
  const [inApp] = useState(() => detectInAppBrowser());
  return (
    <TelegramBotLogin
      botUsername={botUsername}
      surface={surface}
      inApp={inApp}
      onAuth={onBotAuth}
      onError={onBotError}
      className={block ? 'w-full' : ''}
    >
      <span
        className={`inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-gradient-to-b from-telegram to-telegram-deep font-bold text-white shadow-m-sm ${
          compact ? 'h-7 px-2.5 text-[12px]' : 'h-11 px-4 text-[13px]'
        }`}
      >
        <TelegramIcon className={compact ? 'h-3.5 w-3.5 shrink-0' : 'h-[18px] w-[18px] shrink-0'} />
        {t('telegram.connect.cta')}
      </span>
    </TelegramBotLogin>
  );
}
